<template>
  <div
    class="min-h-screen bg-background pb-44"
    @touchstart.passive="onPullStart"
    @touchmove.passive="onPullMove"
    @touchend.passive="onPullEnd"
  >
    <!-- Pull-to-refresh indicator -->
    <div
      class="flex items-center justify-center overflow-hidden"
      :class="{ 'transition-all duration-300': !isPulling }"
      :style="{ height: `${pullIndicatorHeight}px` }"
    >
      <div v-if="isRefreshing" class="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      <span v-else-if="isPulling && pullDistance > 0" class="text-xs text-textMuted">
        {{ pullDistance >= PULL_THRESHOLD ? '放開刷新' : '下拉刷新' }}
      </span>
    </div>

    <!-- Header -->
    <div class="sticky top-0 bg-background/95 backdrop-blur-sm z-10 px-6 pt-safe pb-2">
      <div class="pt-12">
      <div class="flex justify-between items-center mb-4">
        <div class="flex items-center gap-3 relative" @click="toggleSwitcher">
          <div
            class="w-10 h-10 rounded-full flex items-center justify-center text-3xl border-2 border-white shadow-sm"
            :class="currentBaby?.gender === 'boy' ? 'bg-blue-100' : 'bg-pink-100'"
          >
            {{ currentBaby?.gender === 'boy' ? '👦' : '👧' }}
          </div>
          <div>
            <div class="flex items-center gap-1 font-bold text-lg text-textMain">
              {{ currentBaby?.nickname || '載入中...' }}
              <span v-if="store.babies.length > 1" class="text-xs text-textMuted">▼</span>
            </div>
            <div class="text-xs text-textMuted">查看疫苗時程</div>
          </div>

          <!-- Baby Switcher -->
          <div
            v-if="showSwitcher"
            class="absolute top-14 left-0 bg-white rounded-xl shadow-xl p-2 w-48 border border-gray-100 animate-in fade-in slide-in-from-top-2"
          >
            <div
              v-for="b in store.babies"
              :key="b.id"
              @click.stop="switchBaby(b.id)"
              class="p-3 hover:bg-gray-50 rounded-lg flex items-center gap-2 cursor-pointer"
            >
              <span>{{ b.gender === 'boy' ? '👦' : '👧' }}</span>
              <span class="font-bold text-sm">{{ b.nickname }}</span>
            </div>
            <div class="border-t my-1"></div>
            <div
              @click.stop="router.push('/create')"
              class="p-3 text-primary font-bold text-sm text-center cursor-pointer"
            >
              + 新增寶寶
            </div>
          </div>
        </div>

        <button
          @click="router.push('/create')"
          class="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center shadow-md active:scale-95"
          aria-label="新增寶寶"
        >
          <span class="text-xl font-bold">+</span>
        </button>
      </div>

      <div v-if="!showHiddenRecords">
        <div class="flex items-center justify-between gap-2">
          <div class="flex gap-1 rounded-full bg-white p-1 border border-gray-100" role="group" aria-label="接種狀態">
            <button
              v-for="s in STATUS_FILTER_OPTIONS"
              :key="s"
              @click="statusFilter = s"
              class="px-3 min-h-10 rounded-full text-xs font-bold whitespace-nowrap transition-colors"
              :class="statusFilter === s ? 'bg-primary text-textMain' : 'text-textMuted'"
              :aria-pressed="statusFilter === s"
            >
              {{ s === 'all' ? '全部' : s === 'notCompleted' ? '未打' : '已打' }}
            </button>
          </div>
          <button
            @click="showFeeFilter = !showFeeFilter"
            class="flex items-center justify-center gap-1.5 min-h-11 px-3 rounded-xl text-xs font-bold whitespace-nowrap border transition-colors"
            :class="filter !== 'all' || showFeeFilter ? 'bg-secondary/40 border-primary text-textMain' : 'bg-white border-gray-200 text-textMuted'"
            :aria-expanded="showFeeFilter"
            aria-controls="vaccine-fee-filter"
            :aria-label="filter === 'all' ? '篩選費用類別' : `篩選費用類別，目前為${filter === 'public' ? '公費' : '自費'}`"
          >
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
              <path d="M4 7h16M7 12h10M10 17h4" stroke-linecap="round" />
            </svg>
            {{ filter === 'all' ? '篩選' : filter === 'public' ? '公費' : '自費' }}
          </button>
        </div>

        <div v-if="showFeeFilter" id="vaccine-fee-filter" class="mt-3 rounded-2xl border border-gray-100 bg-white p-3">
          <p class="text-xs text-textMuted mb-2">費用類別</p>
          <div class="flex gap-2" role="group" aria-label="費用類別">
            <button
              v-for="t in FILTER_OPTIONS"
              :key="t"
              @click="selectFeeFilter(t)"
              class="flex-1 min-h-11 rounded-xl text-xs font-bold transition-colors"
              :class="filter === t ? 'bg-secondary text-textMain' : 'bg-background text-textMuted'"
              :aria-pressed="filter === t"
            >
              {{ t === 'all' ? '不限費用' : t === 'public' ? '公費' : '自費' }}
            </button>
          </div>
        </div>

        <div class="flex justify-end mt-1">
          <button @click="openHiddenRecords" class="min-h-11 text-xs text-textMuted underline underline-offset-4">
            查看已隱藏項目<span v-if="manuallyHiddenRecords.length">（{{ manuallyHiddenRecords.length }}）</span>
          </button>
        </div>
      </div>
      <div v-else>
        <div class="flex items-center justify-between gap-3">
          <h2 class="font-bold text-textMain">已隱藏項目（{{ manuallyHiddenRecords.length }}）</h2>
          <button @click="showHiddenRecords = false" class="min-h-11 text-xs font-bold text-textMuted underline underline-offset-4">
            返回疫苗清單
          </button>
        </div>
        <p class="text-xs text-textMuted pb-2">這裡保留手動隱藏的項目，可隨時復原。</p>
      </div>
      </div>
    </div>

    <div class="px-6 py-2">
      <NextVaccine v-if="!showHiddenRecords" :records="filteredRecords" />

      <!-- 媽媽孕期 RSV 疫苗卡片（置頂，自費或全部篩選時顯示） -->
      <MaternalRSVCard
        v-if="currentBaby && !showHiddenRecords && (filter === 'all' || filter === 'self')"
        :baby-id="currentBaby.id"
      />

      <div class="space-y-4">
        <div
          v-for="record in filteredRecords"
          :key="record.id"
          :id="`vaccine-card-${record.id}`"
          class="bg-white rounded-2xl p-4 shadow-sm border border-gray-50 relative overflow-hidden transition-all"
          :class="{ 'opacity-60 grayscale-[0.5]': record.status === 'completed' }"
        >
          <!-- Status Line -->
          <div
            class="absolute left-0 top-0 bottom-0 w-1.5"
            :class="{
              'bg-green-400': record.status === 'completed',
              'bg-red-400': record.status === 'overdue',
              'bg-secondary': record.status === 'planned',
              'bg-amber-400': record.status === 'expired',
              'bg-gray-400': record.status === 'skipped'
            }"
          ></div>

          <div>
            <div class="flex justify-between items-start gap-2 mb-2">
              <div class="min-w-0">
                <h3 class="font-bold text-textMain text-lg">
                  {{ getVaccineName(record.vaccineId) }}
                </h3>
                <div class="text-xs bg-gray-100 inline-block px-2 py-0.5 rounded-md mt-1">
                  {{ getDoseLabel(record.doseId) }} · {{ getAgeLabel(record.doseId) }}
                </div>
              </div>

              <div class="flex items-center gap-0.5 flex-shrink-0">
                <VaccineTypeBadge :type="getRecordVaccineType(record)" />
                <button
                  @click="goToVaccineDetail(record)"
                  class="text-teal-500 text-xl w-8 h-8 flex items-center justify-center"
                  :aria-label="`${getVaccineName(record.vaccineId)}疫苗資訊`"
                >
                  ⓘ
                </button>
              </div>
            </div>

            <div
              v-if="record.status === 'expired'"
              class="mt-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 flex items-start gap-2"
            >
              <span class="text-amber-500 text-sm leading-none flex-shrink-0 mt-0.5">⚠️</span>
              <p class="text-xs text-amber-900 leading-relaxed">
                已超過建議接種年齡上限，請諮詢醫師是否仍可接種
              </p>
            </div>

            <div class="flex justify-between items-center mt-4 border-t pt-3 gap-1">
              <div class="flex-1 min-w-0">
                <p class="text-xs text-textMuted whitespace-nowrap">
                  {{ record.status === 'completed' ? '已接種日期' : record.status === 'skipped' ? '狀態' : '預計日期' }}
                </p>
                <p
                  v-if="record.status === 'completed'"
                  class="font-bold font-mono text-sm whitespace-nowrap"
                >
                  {{ record.actualDate || record.plannedDate }}
                </p>
                <p
                  v-else-if="record.status === 'skipped'"
                  class="font-bold text-sm text-textMuted whitespace-nowrap"
                >
                  手動標記不接種
                </p>
                <div
                  v-else
                  class="flex items-center gap-0.5 min-[360px]:gap-1.5 mt-0.5 cursor-pointer active:scale-95 transition-transform"
                  @click.stop="openDatePicker(record)"
                >
                  <span class="font-bold font-mono text-xs min-[360px]:text-sm text-textMain whitespace-nowrap">
                    {{ record.customPlannedDate ?? record.plannedDate }}
                  </span>
                  <span class="w-4 h-4 min-[360px]:w-5 min-[360px]:h-5 flex-shrink-0 rounded-full bg-primary/20 flex items-center justify-center">
                    <span class="text-[10px] text-primary">✎</span>
                  </span>
                </div>
                <p
                  v-if="record.status !== 'completed' && record.status !== 'expired' && record.status !== 'skipped' && record.customPlannedDate && record.customPlannedDate !== record.plannedDate"
                  class="text-[10px] text-textMuted mt-1 flex flex-wrap items-center gap-x-1"
                >
                  原建議 {{ record.plannedDate }}
                  <span
                    class="text-primary underline cursor-pointer"
                    @click.stop="resetCustomDate(record)"
                  >重設</span>
                </p>
              </div>

              <div v-if="record.status !== 'completed' && record.status !== 'skipped'" class="flex items-center gap-1 flex-shrink-0">
                <button
                  v-if="record.status !== 'expired'"
                  @click="handleCalendarClick(record)"
                  @touchstart="handleTouchStart(record)"
                  @touchend="handleTouchEnd"
                  @touchcancel="handleTouchEnd"
                  @mousedown="handleMouseDown(record)"
                  @mouseup="handleMouseUp"
                  @mouseleave="handleMouseUp"
                  class="w-8 min-[360px]:w-10 min-[390px]:w-[72px] h-10 rounded-xl border border-gray-200 flex items-center justify-center"
                  aria-label="加入行事曆"
                >
                  <span class="text-2xl leading-none">
                    {{ record.calendarSynced ? '✓' : '📅' }}
                  </span>
                </button>

                <VaccinationActionButton
                  @confirm="openConfirmModal(record)"
                  @skip="openManualSkipDialog(record)"
                />
              </div>
              <button
                v-else-if="record.status === 'skipped'"
                @click="openManualRestoreDialog(record)"
                class="text-primary text-sm font-bold flex-shrink-0 whitespace-nowrap cursor-pointer hover:underline active:scale-95 transition-transform"
              >
                復原
              </button>
              <button
                v-else
                @click="openConfirmModal(record)"
                class="text-green-600 text-sm font-bold flex-shrink-0 whitespace-nowrap cursor-pointer hover:underline active:scale-95 transition-transform"
              >
                ✓ 已完成
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="showHiddenRecords && filteredRecords.length === 0"
        class="rounded-2xl bg-white px-5 py-12 text-center text-textMuted shadow-sm border border-gray-50"
      >
        目前沒有已隱藏的接種項目
      </div>
      
      <!-- 底部空白區塊，確保最後一張卡片不被 BottomTabBar 遮擋 -->
      <div style="height: 5px;"></div>
    </div>

    <ConfirmVaccinationModal
      :show="showConfirmModal"
      :record="selectedRecord"
      :vaccine-name="selectedRecordVaccineName"
      :dose-label="selectedRecordDoseLabel"
      :earliest-valid-date="selectedRecordEarliestValidDate"
      :loading="confirmModalLoading"
      @close="closeConfirmModal"
      @confirm="handleConfirmVaccination"
      @unconfirm="handleUnconfirmRequest"
    />

    <!-- 改回未接種二次確認 -->
    <ConfirmDialog
      :show="showUnconfirmDialog"
      title="改回未接種"
      message="此劑將改回未接種狀態，後續劑次的預計日期會重新計算。若先前已加入 Google/Apple 行事曆的提醒事件，App 無法自動刪除，請自行到行事曆中移除。"
      confirm-text="改回未接種"
      danger
      :loading="unconfirmLoading"
      @confirm="handleUnconfirmConfirm"
      @cancel="handleUnconfirmCancel"
    />

    <ConfirmDialog
      :show="showManualSkipDialog"
      title="標記為不接種"
      message="此劑將從一般清單與 App 的本機提醒中移除，之後可點選「查看已隱藏項目」隨時復原。若已加入 Google 或 Apple 行事曆，App 無法自動刪除該事件，請自行移除。"
      confirm-text="仍不接種"
      cancel-text="返回"
      warm
      :loading="manualSkipLoading"
      @confirm="handleManualSkipConfirm"
      @cancel="handleManualSkipCancel"
    >
      <div v-if="manualSkipImpacts.all.length" class="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-950">
        <p class="font-bold mb-2">{{ manualSkipImpacts.schedule.length ? '此劑會影響後續接種時程' : '此劑與其他接種項目有關' }}</p>
        <p v-if="manualSkipImpacts.schedule.length">標記「不接種」不代表已完成接種。缺少實際接種日期，後續預計日期可能不準確。</p>
        <p v-if="manualSkipImpacts.related.length" :class="{ 'mt-2': manualSkipImpacts.schedule.length }">選擇「不接種」只會隱藏此劑，其他相關項目可能仍會顯示及提醒。</p>
        <p class="mt-2">如果以前已經打過，建議返回「確認接種」補登日期。</p>
        <details class="mt-3 text-xs">
          <summary class="cursor-pointer font-medium">可能影響的項目（{{ manualSkipImpacts.all.length }}）</summary>
          <ul class="mt-2 space-y-1 list-disc pl-4">
            <li v-for="item in manualSkipImpacts.all" :key="item.id">{{ getVaccineDetails(item.vaccineId)?.name }} · {{ getDoseDetails(item.doseId)?.label }}</li>
          </ul>
        </details>
      </div>
    </ConfirmDialog>

    <ConfirmDialog
      :show="showManualRestoreDialog"
      title="復原接種提醒"
      message="若此劑仍受系統接種規則影響，會保留略過狀態；否則會改回未接種，並依預計日期恢復提醒。"
      confirm-text="確認復原"
      :loading="manualRestoreLoading"
      @confirm="handleManualRestoreConfirm"
      @cancel="handleManualRestoreCancel"
    />

    <!-- 日曆功能相關對話框 -->
    <CalendarChoiceDialog
      :show="showCalendarChoice"
      @close="closeCalendarChoice"
      @choose="handleCalendarChoice"
    />

    <CalendarTipDialog
      :show="showCalendarTip"
      @close="closeCalendarTip"
    />

    <CalendarMarkedDialog
      :show="showCalendarMarked"
      @close="closeCalendarMarked"
    />

    <CalendarOptionsDialog
      :show="showCalendarOptions"
      @close="closeCalendarOptions"
      @reopen="reopenCalendar"
      @unmark="unmarkCalendar"
    />

    <!-- 自訂預計日期 picker -->
    <Transition name="dialog">
      <div
        v-if="showDatePicker"
        class="fixed inset-0 bg-black/40 flex items-end justify-center z-[60]"
        @click.self="closeDatePicker"
      >
        <div class="bg-white rounded-t-3xl w-full max-w-lg shadow-2xl pb-safe" @click.stop>
          <!-- 拖曳指示條 -->
          <div class="flex justify-center pt-3 pb-2">
            <div class="w-10 h-1 bg-gray-300 rounded-full"></div>
          </div>

          <div class="px-6 pb-6">
            <!-- 標題區 -->
            <div class="flex items-center gap-3 mb-5">
              <div class="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                <span class="text-lg">📅</span>
              </div>
              <div>
                <h3 class="text-base font-bold text-textMain">設定預計施打日期</h3>
                <p class="text-xs text-textMuted">
                  {{ datePickerVaccineName }} {{ datePickerDoseLabel }}
                </p>
              </div>
            </div>

            <!-- 建議日期提示 -->
            <div class="bg-background rounded-xl px-4 py-3 mb-4 flex items-center justify-between">
              <span class="text-xs text-textMuted">系統建議日期</span>
              <span class="text-sm font-mono font-bold text-textMain">
                {{ datePickerRecord?.plannedDate }}
              </span>
            </div>

            <!-- 日期選擇 -->
            <label class="text-xs text-textMuted mb-2 block">選擇預計日期</label>
            <div class="border-2 border-gray-200 rounded-xl px-4 py-3 bg-white focus-within:border-primary transition-colors overflow-hidden">
              <input
                type="date"
                v-model="datePickerValue"
                class="w-full text-base font-mono bg-transparent outline-none"
              />
            </div>

            <!-- 早於建議日警告 -->
            <div
              v-if="datePickerWarning"
              class="mt-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2"
            >
              <span class="text-sm mt-0.5">⚠️</span>
              <p class="text-xs text-amber-700 leading-relaxed">
                此日期早於建議施打日，請先與醫師確認是否可提前施打。
              </p>
            </div>

            <!-- 按鈕 -->
            <div class="flex gap-3 mt-6">
              <button
                @click="closeDatePicker"
                class="flex-1 py-3.5 rounded-xl font-bold text-textMuted bg-gray-100 active:scale-95 transition-all"
              >
                取消
              </button>
              <button
                @click="confirmDatePicker"
                class="flex-1 py-3.5 rounded-xl font-bold text-white bg-primary active:scale-95 transition-all disabled:opacity-40"
                :disabled="!datePickerValue"
              >
                確定
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- 一鍵至頂按鈕 -->
    <Transition name="scroll-top-fade">
      <button
        v-if="showScrollTopBtn"
        @click="scrollToTop"
        class="fixed bottom-32 right-6 w-12 h-12 bg-primary text-white rounded-full shadow-lg flex items-center justify-center z-20 active:scale-95 transition-transform"
        aria-label="回到頂部"
      >
        <span class="text-2xl">↑</span>
      </button>
    </Transition>

    <!-- Toast 提示 -->
    <Transition name="toast">
      <div
        v-if="toast.show"
        class="fixed bottom-28 left-1/2 -translate-x-1/2 w-max max-w-[85%] bg-textMain text-white px-5 py-3 rounded-xl shadow-lg z-50 flex items-center gap-4"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <span class="text-sm">{{ toast.message }}</span>
        <button
          v-if="toast.undoRecordId"
          @click="undoManualSkip"
          :disabled="manualRestoreLoading"
          class="min-h-11 flex-shrink-0 text-secondary text-sm font-bold underline underline-offset-4 disabled:opacity-50"
        >
          {{ manualRestoreLoading ? '復原中…' : '復原' }}
        </button>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useAppStore } from '../stores/useAppStore'
import { useVaccineDefinitions } from '../stores/useVaccineDefinitions'
import type { VaccinationRecord } from '../types'
import { getVaccineDetails, getDoseDetails, generateCalendarUrl, generateICSContent } from '../services/scheduleService'
import { getEarliestValidVaccinationDate } from '../utils/vaccineRules'
import { getEffectiveDate } from '../utils/vaccineDate'
import { getManualSkipImpacts } from '../utils/vaccineDependencies'
import { format } from 'date-fns'
import ConfirmVaccinationModal from '../components/ConfirmVaccinationModal.vue'
import ConfirmDialog from '../components/ConfirmDialog.vue'
import MaternalRSVCard from '../components/MaternalRSVCard.vue'
import NextVaccine from '../components/NextVaccine.vue'
import VaccineTypeBadge from '../components/VaccineTypeBadge.vue'
import VaccinationActionButton from '../components/VaccinationActionButton.vue'
import CalendarChoiceDialog from '../components/CalendarChoiceDialog.vue'
import CalendarTipDialog from '../components/CalendarTipDialog.vue'
import CalendarMarkedDialog from '../components/CalendarMarkedDialog.vue'
import CalendarOptionsDialog from '../components/CalendarOptionsDialog.vue'
import { App } from '@capacitor/app'
import { Capacitor, type PluginListenerHandle } from '@capacitor/core'
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem'
import { Share } from '@capacitor/share'

const router = useRouter()
const store = useAppStore()
const vaccineDefinitions = useVaccineDefinitions()

const FILTER_OPTIONS = ['all', 'public', 'self'] as const
const filter = ref<typeof FILTER_OPTIONS[number]>('all')

const STATUS_FILTER_OPTIONS = ['all', 'notCompleted', 'completed'] as const
const statusFilter = ref<typeof STATUS_FILTER_OPTIONS[number]>('all')
const showFeeFilter = ref(false)
const showHiddenRecords = ref(false)

const selectFeeFilter = (value: typeof FILTER_OPTIONS[number]) => {
  filter.value = value
  showFeeFilter.value = false
}

const openHiddenRecords = () => {
  showFeeFilter.value = false
  showHiddenRecords.value = true
}

const showSwitcher = ref(false)

// 一鍵至頂按鈕控制
const showScrollTopBtn = ref(false)
let appStateListener: PluginListenerHandle | null = null
let isUnmounted = false

/* 核心修正：只從雲端初始化 */
onMounted(async () => {
  await store.initFromCloud()
  if (isUnmounted) return

  // 監聽 App Resume 事件（從背景回到前景）
  const listener = await App.addListener('appStateChange', handleAppStateChange)
  if (isUnmounted) {
    await listener.remove()
    return
  }
  appStateListener = listener

  // 監聽滾動事件，控制「一鍵至頂」按鈕顯示
  window.addEventListener('scroll', handleScroll)

  // 檢查是否需要滾動到特定卡片（從詳細頁面返回）
  nextTick(() => {
    const scrollToCardId = sessionStorage.getItem('scrollToCard')
    if (scrollToCardId) {
      sessionStorage.removeItem('scrollToCard')
      scrollToVaccineCard(scrollToCardId)
    }
  })
})

onUnmounted(() => {
  isUnmounted = true
  // 移除監聽器
  appStateListener?.remove()
  appStateListener = null
  window.removeEventListener('scroll', handleScroll)
  if (toastTimer) clearTimeout(toastTimer)
})

// 處理 App 狀態變化
const handleAppStateChange = (state: { isActive: boolean }) => {
  if (state.isActive) {
    // App 回到前景，檢查是否剛從日曆返回
    const justOpenedCalendar = sessionStorage.getItem('justOpenedCalendar')
    if (justOpenedCalendar === 'true') {
      // 清除標記
      sessionStorage.removeItem('justOpenedCalendar')
      // 顯示首次提示
      checkAndShowCalendarTip()
    }
  }
}

// 檢查並顯示日曆提示（首次使用）
const checkAndShowCalendarTip = () => {
  const hasShownTip = localStorage.getItem('hasShownCalendarTip')
  if (!hasShownTip) {
    showCalendarTip.value = true
    localStorage.setItem('hasShownCalendarTip', 'true')
  }
}

const currentBaby = computed(() =>
  store.babies.find(b => b.id === store.currentBabyId) ?? null
)

const manuallyHiddenRecords = computed(() =>
  store.currentBabyRecords.filter(r => r.status === 'skipped' && r.manualSkip === true)
)

const getRecordVaccineType = (record: VaccinationRecord) =>
  getDoseDetails(record.doseId)?.type ?? getVaccineDetails(record.vaccineId)?.type

const filteredRecords = computed(() => {
  // 找回頁獨立呈現所有手動隱藏項目，不套用主清單的狀態或費用篩選。
  if (showHiddenRecords.value) return manuallyHiddenRecords.value

  return store.currentBabyRecords.filter(r => {
    if (r.status === 'skipped') return false

    if (statusFilter.value === 'completed' && r.status !== 'completed') return false
    // 未打：涵蓋 planned/overdue/expired，過齡仍需諮詢醫師的也算未打
    if (statusFilter.value === 'notCompleted' && r.status === 'completed') return false

    const v = getVaccineDetails(r.vaccineId)
    if (!v) return false
    if (filter.value === 'all') return true
    const effectiveType = getRecordVaccineType(r)
    if (filter.value === 'public') return effectiveType !== 'self'
    if (filter.value === 'self') return effectiveType === 'self'
    return true
  })
})

const getVaccineName = (id: string) => getVaccineDetails(id)?.name ?? ''
const getDoseLabel = (id: string) => getDoseDetails(id)?.label ?? ''
const getAgeLabel = (id: string) => getDoseDetails(id)?.customAgeLabel ?? ''

const toggleSwitcher = () => {
  if (store.babies.length > 1) showSwitcher.value = !showSwitcher.value
}

const switchBaby = (id: string) => {
  store.switchBaby(id)
  showSwitcher.value = false
}

// ===== 疫苗詳細頁面跳轉 =====

const goToVaccineDetail = (record: VaccinationRecord) => {
  // 儲存當前卡片 ID，用於返回時滾動
  sessionStorage.setItem('scrollToCard', record.id)
  // 跳轉到疫苗詳細頁面
  router.push(`/vaccine/${record.vaccineId}`)
}

const scrollToVaccineCard = (recordId: string) => {
  const cardElement = document.getElementById(`vaccine-card-${recordId}`)
  if (cardElement) {
    // 延遲滾動，確保 DOM 已完全渲染
    setTimeout(() => {
      cardElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
      // 添加短暫的高亮效果
      cardElement.classList.add('highlight-card')
      setTimeout(() => {
        cardElement.classList.remove('highlight-card')
      }, 1500)
    }, 100)
  }
}

// ===== 下拉刷新 =====

const PULL_THRESHOLD = 60
const pullStartY = ref(0)
const pullDistance = ref(0)
const isPulling = ref(false)
const isRefreshing = ref(false)

const pullIndicatorHeight = computed(() =>
  isRefreshing.value ? 48 : pullDistance.value
)

const onPullStart = (e: TouchEvent) => {
  if (window.scrollY === 0 && e.touches[0]) {
    pullStartY.value = e.touches[0].clientY
    isPulling.value = true
  }
}

const onPullMove = (e: TouchEvent) => {
  if (!isPulling.value || !e.touches[0]) return
  const delta = e.touches[0].clientY - pullStartY.value
  pullDistance.value = delta > 0 ? Math.min(delta * 0.5, PULL_THRESHOLD * 1.5) : 0
}

const onPullEnd = async () => {
  if (!isPulling.value) return
  isPulling.value = false
  if (pullDistance.value >= PULL_THRESHOLD) {
    pullDistance.value = PULL_THRESHOLD
    isRefreshing.value = true
    try {
      await vaccineDefinitions.checkForUpdates()
      if (vaccineDefinitions.hasUpdate && store.babies.length > 0) {
        await store.updateVaccineRecordsToNewVersion()
        vaccineDefinitions.clearUpdateFlag()
      }
      await store.initFromCloud()
    } finally {
      isRefreshing.value = false
      pullDistance.value = 0
    }
  } else {
    pullDistance.value = 0
  }
}

// ===== 一鍵至頂功能 =====

const handleScroll = () => {
  // 當滾動超過 300px 時顯示「回到頂部」按鈕
  showScrollTopBtn.value = window.scrollY > 300
}

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

// ===== 日曆功能相關 =====

// iOS 行事曆偏好（'google' | 'apple' | null）
function readCalendarPreference(): 'google' | 'apple' | null {
  const v = localStorage.getItem('calendarPreference')
  return v === 'google' || v === 'apple' ? v : null
}
const calendarPreference = ref<'google' | 'apple' | null>(readCalendarPreference())
const isIOS = Capacitor.getPlatform() === 'ios'

// Toast 提示
const toast = reactive({ show: false, message: '', undoRecordId: null as string | null })
let toastTimer: ReturnType<typeof setTimeout> | null = null
const showToast = (message: string, undoRecordId: string | null = null) => {
  if (toastTimer) clearTimeout(toastTimer)
  toast.message = message
  toast.undoRecordId = undoRecordId
  toast.show = true
  toastTimer = setTimeout(() => { toast.show = false }, undoRecordId ? 8000 : 2500)
}

// 對話框狀態
const showCalendarChoice = ref(false)
const showCalendarTip = ref(false)
const showCalendarMarked = ref(false)
const showCalendarOptions = ref(false)

// 長按檢測
let longPressTimer: ReturnType<typeof setTimeout> | null = null
let isLongPress = false
const LONG_PRESS_DURATION = 500 // 500ms 判定為長按

// 當前操作的 record
const currentCalendarRecord = ref<VaccinationRecord | null>(null)

// 處理觸控開始（移動端）
const handleTouchStart = (record: VaccinationRecord) => {
  isLongPress = false
  currentCalendarRecord.value = record

  longPressTimer = setTimeout(() => {
    isLongPress = true
    handleLongPress(record)
  }, LONG_PRESS_DURATION)
}

// 處理觸控結束
const handleTouchEnd = () => {
  if (longPressTimer) {
    clearTimeout(longPressTimer)
    longPressTimer = null
  }
}

// 處理滑鼠按下（桌面端）
const handleMouseDown = (record: VaccinationRecord) => {
  isLongPress = false
  currentCalendarRecord.value = record

  longPressTimer = setTimeout(() => {
    isLongPress = true
    handleLongPress(record)
  }, LONG_PRESS_DURATION)
}

// 處理滑鼠放開
const handleMouseUp = () => {
  if (longPressTimer) {
    clearTimeout(longPressTimer)
    longPressTimer = null
  }
}

// 處理長按：直接標記為已設定
const handleLongPress = (record: VaccinationRecord) => {
  if (record.calendarSynced) {
    return // 已標記則不處理
  }

  // 標記為已設定
  store.toggleCalendarSync(record.id)

  // 檢查是否是首次標記，如果是則顯示提示
  const hasShownMarkedTip = localStorage.getItem('hasShownCalendarMarkedTip')

  if (!hasShownMarkedTip) {
    showCalendarMarked.value = true
    localStorage.setItem('hasShownCalendarMarkedTip', 'true')
  }
}

// 處理點擊日曆按鈕
const handleCalendarClick = (record: VaccinationRecord) => {
  if (isLongPress) {
    isLongPress = false
    return
  }

  if (record.calendarSynced) {
    currentCalendarRecord.value = record
    showCalendarOptions.value = true
  } else {
    currentCalendarRecord.value = record
    // iOS 且尚未選擇偏好：先問
    if (isIOS && calendarPreference.value === null) {
      showCalendarChoice.value = true
    } else {
      openPreferredCalendar(record)
    }
  }
}

// 使用者在 CalendarChoiceDialog 選擇行事曆
const handleCalendarChoice = (provider: 'google' | 'apple') => {
  localStorage.setItem('calendarPreference', provider)
  calendarPreference.value = provider
  // 第一次選擇後自動跳過 CalendarTipDialog
  localStorage.setItem('hasShownCalendarTip', 'true')
  showCalendarChoice.value = false
  if (currentCalendarRecord.value) {
    openPreferredCalendar(currentCalendarRecord.value)
  }
}

// 依偏好開啟行事曆
const openPreferredCalendar = (record: VaccinationRecord) => {
  if (calendarPreference.value === 'apple') {
    openAppleCalendar(record)
  } else {
    openGoogleCalendar(record)
  }
}

// 打開 Google Calendar
const openGoogleCalendar = (record: VaccinationRecord) => {
  const baby = currentBaby.value
  if (!baby) return
  const url = generateCalendarUrl(record, baby.nickname)
  if (!url) return

  sessionStorage.setItem('justOpenedCalendar', 'true')
  window.open(url, '_blank', 'noopener,noreferrer')
}

// 打開 Apple Calendar（ICS 分享）
const openAppleCalendar = async (record: VaccinationRecord) => {
  const baby = currentBaby.value
  if (!baby) return
  const icsContent = generateICSContent(record, baby.nickname)
  if (!icsContent) return

  try {
    await Filesystem.writeFile({
      path: 'vaccine-event.ics',
      data: icsContent,
      directory: Directory.Cache,
      encoding: Encoding.UTF8
    })
    const { uri } = await Filesystem.getUri({
      path: 'vaccine-event.ics',
      directory: Directory.Cache
    })
    await Share.share({ files: [uri] })
  } catch (err: any) {
    // 使用者取消分享不算錯誤（iOS 取消時會拋出 AbortError）
    if (err?.errorMessage !== 'Share canceled' && err?.name !== 'AbortError') {
      console.error('[Calendar] Apple Calendar 分享失敗:', err)
      showToast('無法開啟行事曆，請稍後再試')
    }
  }
}

// 重新打開日曆（從選項對話框）
const reopenCalendar = () => {
  if (currentCalendarRecord.value) {
    openPreferredCalendar(currentCalendarRecord.value)
  }
}

// 取消標記（從選項對話框）
const unmarkCalendar = () => {
  if (currentCalendarRecord.value) {
    store.toggleCalendarSync(currentCalendarRecord.value.id)
  }
}

// 關閉對話框
const closeCalendarChoice = () => {
  showCalendarChoice.value = false
}

const closeCalendarTip = () => {
  showCalendarTip.value = false
}

const closeCalendarMarked = () => {
  showCalendarMarked.value = false
}

const closeCalendarOptions = () => {
  showCalendarOptions.value = false
  currentCalendarRecord.value = null
}

// ===== 自訂預計日期 =====

const showDatePicker = ref(false)
const datePickerValue = ref('')
const datePickerRecord = ref<VaccinationRecord | null>(null)

const datePickerVaccineName = computed(() =>
  datePickerRecord.value ? getVaccineName(datePickerRecord.value.vaccineId) : ''
)
const datePickerDoseLabel = computed(() =>
  datePickerRecord.value ? getDoseLabel(datePickerRecord.value.doseId) : ''
)
const datePickerWarning = computed(() => {
  if (!datePickerRecord.value || !datePickerValue.value) return false
  return datePickerValue.value < datePickerRecord.value.plannedDate
})

const openDatePicker = (record: VaccinationRecord) => {
  datePickerRecord.value = record
  datePickerValue.value = record.customPlannedDate ?? record.plannedDate
  showDatePicker.value = true
}

const closeDatePicker = () => {
  showDatePicker.value = false
  datePickerRecord.value = null
}

const confirmDatePicker = async () => {
  if (!datePickerRecord.value || !datePickerValue.value) return

  const record = datePickerRecord.value
  const newDate = datePickerValue.value

  // 如果選的日期跟系統建議日一樣，視為清除自訂
  if (newDate === record.plannedDate) {
    await store.updateCustomPlannedDate(record.id, null)
  } else {
    await store.updateCustomPlannedDate(record.id, newDate)
  }

  closeDatePicker()
}

const resetCustomDate = async (record: VaccinationRecord) => {
  await store.updateCustomPlannedDate(record.id, null)
}

// ===== 接種確認相關 =====

const showConfirmModal = ref(false)
const selectedRecord = ref<VaccinationRecord | null>(null)

const openConfirmModal = (record: VaccinationRecord) => {
  selectedRecord.value = record
  showConfirmModal.value = true
}

const closeConfirmModal = () => {
  showConfirmModal.value = false
  selectedRecord.value = null
}

const selectedRecordVaccineName = computed(() =>
  selectedRecord.value ? getVaccineName(selectedRecord.value.vaccineId) : ''
)

const selectedRecordDoseLabel = computed(() =>
  selectedRecord.value ? getDoseLabel(selectedRecord.value.doseId) : ''
)

// 「提前接種視同無效」規則：僅查核範圍內的疫苗（HepB/五合一/PCV13/HepA）會回傳非 null
const selectedRecordEarliestValidDate = computed(() => {
  const record = selectedRecord.value
  const baby = currentBaby.value
  if (!record || !baby) return null

  const dose = vaccineDefinitions.getDoseById(record.doseId)
  if (!dose) return null

  const effectiveDatesByDoseId = new Map<string, Date>()
  for (const r of store.currentBabyRecords) {
    effectiveDatesByDoseId.set(r.doseId, new Date(getEffectiveDate(r)))
  }

  const earliest = getEarliestValidVaccinationDate(dose, new Date(baby.birthDate), effectiveDatesByDoseId)
  return earliest ? format(earliest, 'yyyy-MM-dd') : null
})

const confirmModalLoading = ref(false)

const handleConfirmVaccination = async (data: {
  recordId: string
  actualDate: string
  options: {
    rotaType?: '2' | '3'
    fluFirstTime?: boolean
  }
}) => {
  if (confirmModalLoading.value) return
  confirmModalLoading.value = true
  try {
    await store.confirmVaccination(data.recordId, data.actualDate, data.options)
    closeConfirmModal()
  } catch (error) {
    console.error('確認接種失敗:', error)
    showToast('確認接種失敗，請稍後再試')
  } finally {
    confirmModalLoading.value = false
  }
}

// ===== 改回未接種 =====

const showUnconfirmDialog = ref(false)
const unconfirmLoading = ref(false)
const pendingUnconfirmRecordId = ref<string | null>(null)

const handleUnconfirmRequest = (recordId: string) => {
  closeConfirmModal()
  pendingUnconfirmRecordId.value = recordId
  showUnconfirmDialog.value = true
}

const handleUnconfirmCancel = () => {
  showUnconfirmDialog.value = false
  pendingUnconfirmRecordId.value = null
}

const handleUnconfirmConfirm = async () => {
  const recordId = pendingUnconfirmRecordId.value
  if (!recordId || unconfirmLoading.value) return

  unconfirmLoading.value = true
  try {
    await store.unconfirmVaccination(recordId)
    showUnconfirmDialog.value = false
    pendingUnconfirmRecordId.value = null
  } catch (error) {
    console.error('改回未接種失敗:', error)
    showToast('改回未接種失敗，請稍後再試')
  } finally {
    unconfirmLoading.value = false
  }
}

// ===== 手動標記不接種／復原 =====

const showManualSkipDialog = ref(false)
const manualSkipLoading = ref(false)
const pendingManualSkipRecordId = ref<string | null>(null)
const manualSkipImpacts = computed(() => {
  const record = store.currentBabyRecords.find(item => item.id === pendingManualSkipRecordId.value)
  return record ? getManualSkipImpacts(record, store.currentBabyRecords, vaccineDefinitions.getAllDoses()) : { schedule: [], related: [], all: [] }
})

const openManualSkipDialog = (record: VaccinationRecord) => {
  pendingManualSkipRecordId.value = record.id
  showManualSkipDialog.value = true
}

const handleManualSkipCancel = () => {
  showManualSkipDialog.value = false
  pendingManualSkipRecordId.value = null
}

const handleManualSkipConfirm = async () => {
  const recordId = pendingManualSkipRecordId.value
  if (!recordId || manualSkipLoading.value) return

  manualSkipLoading.value = true
  try {
    await store.markVaccinationAsSkipped(recordId)
    showManualSkipDialog.value = false
    pendingManualSkipRecordId.value = null
    showToast('已標記不接種，可至已隱藏項目查看', recordId)
  } catch (error) {
    console.error('標記不接種失敗:', error)
    showToast('標記不接種失敗，請稍後再試')
  } finally {
    manualSkipLoading.value = false
  }
}

const showManualRestoreDialog = ref(false)
const manualRestoreLoading = ref(false)
const pendingManualRestoreRecordId = ref<string | null>(null)

const openManualRestoreDialog = (record: VaccinationRecord) => {
  pendingManualRestoreRecordId.value = record.id
  showManualRestoreDialog.value = true
}

const handleManualRestoreCancel = () => {
  showManualRestoreDialog.value = false
  pendingManualRestoreRecordId.value = null
}

const restoreHiddenRecord = async (recordId: string) => {
  const result = await store.restoreManuallySkippedVaccination(recordId)

  if (result.status === 'still-skipped') {
    const sourceNames = result.sourceDoseIds
      .map(sourceDoseId => {
        const sourceDose = getDoseDetails(sourceDoseId)
        if (!sourceDose) return ''
        const sourceVaccine = getVaccineDetails(sourceDose.vaccineId)
        return `${sourceVaccine?.name ?? ''}${sourceDose.label}`
      })
      .filter(Boolean)
    const sourceDescription = sourceNames.length > 0
      ? sourceNames.join('、')
      : '系統接種規則'

    showToast(`此劑仍受${sourceDescription}影響，維持略過`)
  } else if (result.status === 'restored') {
    showToast('已復原至疫苗清單')
  } else {
    showToast('此項目的狀態已變更，請重新查看清單')
  }
}

const undoManualSkip = async () => {
  const recordId = toast.undoRecordId
  if (!recordId || manualRestoreLoading.value) return

  if (toastTimer) clearTimeout(toastTimer)
  manualRestoreLoading.value = true
  try {
    await restoreHiddenRecord(recordId)
  } catch (error) {
    console.error('復原隱藏項目失敗:', error)
    showToast('復原失敗，請再試一次', recordId)
  } finally {
    manualRestoreLoading.value = false
  }
}

const handleManualRestoreConfirm = async () => {
  const recordId = pendingManualRestoreRecordId.value
  if (!recordId || manualRestoreLoading.value) return

  manualRestoreLoading.value = true
  try {
    await restoreHiddenRecord(recordId)
    showManualRestoreDialog.value = false
    pendingManualRestoreRecordId.value = null

  } catch (error) {
    console.error('復原不接種失敗:', error)
    showToast('復原不接種失敗，請稍後再試')
  } finally {
    manualRestoreLoading.value = false
  }
}
</script>

<style scoped>
@keyframes highlight {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(34, 197, 94, 0);
    transform: scale(1.02);
  }
}

.highlight-card {
  animation: highlight 1s ease-in-out 2;
  border-color: rgb(34, 197, 94) !important;
}

.scroll-top-fade-enter-active,
.scroll-top-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.scroll-top-fade-enter-from,
.scroll-top-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(10px);
}
</style>
